import axios from "axios";
import { firestore } from "../firebase.config";
import { doc, setDoc, getDoc, updateDoc, collection, addDoc, query, where, getDocs, deleteDoc } from "firebase/firestore";

export const generateJWT = async (email, tenantId, userRole) => {
    try {
      const response = await axios.post('https://user-management-api-v1.onrender.com/api/auth/login', {
        email,
        tenantId,
        userRole
      });
      console.log("JWT generated successfully:", response.data); // Log the JWT data
      return response.data; // Return the response data containing the token
    } catch (error) {
      console.error("Error generating JWT:", error.response ? error.response.data : error.message);
      throw error;
    }
  };

// Firestore utilities for QR Events

// Create a QR event document
export const createQREvent = async (eventData) => {
  const colRef = collection(firestore, "QREvents");
  const docRef = await addDoc(colRef, {
    ...eventData,
    createdAt: new Date().toISOString(),
    active: true,
  });
  return { id: docRef.id };
};

// Update an existing QR event
export const updateQREvent = async (eventId, updates) => {
  const ref = doc(firestore, "QREvents", eventId);
  await updateDoc(ref, updates);
  return true;
};

// Fetch a QR event by id
export const getQREventById = async (eventId) => {
  const ref = doc(firestore, "QREvents", eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// List all active QR events for a tenant
export const listActiveQREvents = async (tenantId) => {
  const colRef = collection(firestore, "QREvents");
  const q = query(colRef, where("tenantId", "==", tenantId), where("active", "==", true));
  const res = await getDocs(q);
  return res.docs.map(d => ({ id: d.id, ...d.data() }));
};

// List all QR events for a tenant (active and inactive)
export const listAllQREvents = async (tenantId) => {
  const colRef = collection(firestore, "QREvents");
  const q = query(colRef, where("tenantId", "==", tenantId));
  const res = await getDocs(q);
  return res.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Delete a QR event
export const deleteQREvent = async (eventId) => {
  const ref = doc(firestore, "QREvents", eventId);
  await deleteDoc(ref);
  return true;
};

// Mark events inactive if they are past end time
export const deactivateExpiredEvents = async (tenantId) => {
  const colRef = collection(firestore, "QREvents");
  const q = query(colRef, where("tenantId", "==", tenantId), where("active", "==", true));
  const res = await getDocs(q);
  const now = new Date();
  const updates = [];
  for (const d of res.docs) {
    const data = d.data();
    const end = new Date(`${data.date}T${data.endTime}:00`);
    if (end < now) {
      updates.push(updateDoc(doc(firestore, "QREvents", d.id), { active: false }));
    }
  }
  await Promise.allSettled(updates);
};

// Classroom utilities

// Create a classroom document
export const createClassroom = async (classroomData) => {
  try {
    if (!classroomData.tenantId) {
      throw new Error("Tenant ID is required");
    }
    
    if (!classroomData.name) {
      throw new Error("Classroom name is required");
    }
    
    const colRef = collection(firestore, "Classrooms");
    const docRef = await addDoc(colRef, {
      ...classroomData,
      createdAt: new Date().toISOString(),
    });
    
    console.log(`Created classroom with ID: ${docRef.id}`);
    return { id: docRef.id };
  } catch (error) {
    console.error("Error creating classroom:", error);
    throw error;
  }
};

export const getClassroomById = async (classroomId) => {
  const ref = doc(firestore, "Classrooms", classroomId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// List classrooms by tenant
export const listClassrooms = async (tenantId) => {
  try {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }
    
    const colRef = collection(firestore, "Classrooms");
    const q = query(colRef, where("tenantId", "==", tenantId));
    const res = await getDocs(q);
    
    const classrooms = res.docs.map(d => ({ id: d.id, ...d.data() }));
    console.log(`Found ${classrooms.length} classrooms for tenant ${tenantId}`);
    return classrooms;
  } catch (error) {
    console.error("Error listing classrooms:", error);
    throw error;
  }
};

// Update classroom
export const updateClassroom = async (classroomId, updates) => {
  const ref = doc(firestore, "Classrooms", classroomId);
  await updateDoc(ref, { ...updates, updatedAt: new Date().toISOString() });
  return true;
};

// Delete classroom (guard: ensure no timetable entries exist)
export const deleteClassroom = async (tenantId, classroomId) => {
  // Check timetable entries
  const entries = await listTimetableEntriesByClassroom(tenantId, classroomId);
  if (entries && entries.length > 0) {
    throw new Error("Cannot delete classroom with existing timetable entries. Remove timetable first.");
  }
  await deleteDoc(doc(firestore, "Classrooms", classroomId));
  return true;
};

// List bookings for a classroom on a specific date
export const listClassroomBookings = async (tenantId, classroomId, date) => {
  try {
    console.log(`listClassroomBookings: Searching for bookings`);
    console.log(`listClassroomBookings: tenantId: ${tenantId}, classroomId: ${classroomId}, date: ${date}`);
    
    const colRef = collection(firestore, "QREvents");
    const q = query(
      colRef,
      where("tenantId", "==", tenantId),
      where("type", "==", "classroom"),
      where("classroomId", "==", classroomId),
      where("date", "==", date)
    );
    const res = await getDocs(q);
    const bookings = res.docs.map(d => ({ id: d.id, ...d.data() }));
    
    console.log(`listClassroomBookings: Found ${bookings.length} bookings`);
    bookings.forEach(b => {
      console.log(`listClassroomBookings: Booking ${b.id}: ${b.startTime}-${b.endTime}, active: ${b.active ?? true}`);
    });
    
    return bookings;
  } catch (error) {
    console.error("Error listing classroom bookings:", error);
    throw error;
  }
};

// Check if a time range overlaps any existing bookings for the classroom on that date
export const isClassroomTimeAvailable = async (tenantId, classroomId, date, startTime, endTime) => {
  const bookings = await listClassroomBookings(tenantId, classroomId, date);
  const requestStart = new Date(`${date}T${startTime}:00`).getTime();
  const requestEnd = new Date(`${date}T${endTime}:00`).getTime();
  for (const b of bookings) {
    const bStart = new Date(`${b.date}T${b.startTime}:00`).getTime();
    const bEnd = new Date(`${b.date}T${b.endTime}:00`).getTime();
    // overlap if start < existing end AND end > existing start
    if (requestStart < bEnd && requestEnd > bStart) {
      return { available: false, conflict: b };
    }
  }
  return { available: true };
};

// Helper to create a classroom QR event with conflict checking
export const createClassroomQREvent = async (eventData) => {
  const { tenantId, classroomId, date, startTime, endTime } = eventData;
  if (!tenantId || !classroomId || !date || !startTime || !endTime) {
    throw new Error("Missing required classroom booking fields");
  }
  const { available, conflict } = await isClassroomTimeAvailable(tenantId, classroomId, date, startTime, endTime);
  if (!available) {
    const err = new Error("Classroom time overlaps with an existing booking");
    err.conflict = conflict;
    throw err;
  }
  return await createQREvent({ ...eventData, type: "classroom" });
};

// Debug function to get all bookings for a classroom on a specific date
export const debugGetAllBookingsForClassroom = async (tenantId, classroomId, date) => {
  try {
    console.log(`debugGetAllBookingsForClassroom: Getting all bookings for debugging`);
    console.log(`debugGetAllBookingsForClassroom: tenantId: ${tenantId}, classroomId: ${classroomId}, date: ${date}`);
    
    const bookings = await listClassroomBookings(tenantId, classroomId, date);
    console.log(`debugGetAllBookingsForClassroom: Found ${bookings.length} total bookings`);
    
    bookings.forEach((booking, index) => {
      console.log(`debugGetAllBookingsForClassroom: Booking ${index + 1}:`);
      console.log(`  - ID: ${booking.id}`);
      console.log(`  - Name: ${booking.name}`);
      console.log(`  - Start Time: ${booking.startTime}`);
      console.log(`  - End Time: ${booking.endTime}`);
      console.log(`  - Date: ${booking.date}`);
      console.log(`  - Active: ${booking.active}`);
      console.log(`  - Type: ${booking.type}`);
      console.log(`  - Tenant ID: ${booking.tenantId}`);
      console.log(`  - Classroom ID: ${booking.classroomId}`);
      console.log(`  - Created At: ${booking.createdAt}`);
    });
    
    return bookings;
  } catch (error) {
    console.error("Error in debugGetAllBookingsForClassroom:", error);
    throw error;
  }
};

// Find active classroom booking at now (by current date)
export const findActiveClassroomBookingNow = async (tenantId, classroomId) => {
  try {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const currentTime = now.toTimeString().slice(0, 8); // HH:MM:SS format
    
    console.log(`findActiveClassroomBookingNow: Searching for active booking`);
    console.log(`findActiveClassroomBookingNow: tenantId: ${tenantId}, classroomId: ${classroomId}`);
    console.log(`findActiveClassroomBookingNow: date: ${date}`);
    console.log(`findActiveClassroomBookingNow: current time: ${currentTime} (${currentMinutes} minutes)`);
    
    const bookings = await listClassroomBookings(tenantId, classroomId, date);
    console.log(`findActiveClassroomBookingNow: Found ${bookings.length} bookings for today`);
    
    if (bookings.length === 0) {
      console.log(`findActiveClassroomBookingNow: No bookings found for today`);
      return null;
    }
    
    for (const b of bookings) {
      console.log(`findActiveClassroomBookingNow: Checking booking ${b.id}:`);
      console.log(`  - Name: ${b.name}`);
      console.log(`  - Start Time: ${b.startTime}`);
      console.log(`  - End Time: ${b.endTime}`);
      console.log(`  - Date: ${b.date}`);
      console.log(`  - Active: ${b.active ?? true}`);
      console.log(`  - Type: ${b.type}`);
      
      const [sh, sm] = (b.startTime || "00:00").split(":").map(Number);
      const [eh, em] = (b.endTime || "00:00").split(":").map(Number);
      const startM = sh * 60 + sm;
      const endM = eh * 60 + em;
      
      console.log(`  - Start minutes: ${startM} (${sh}:${sm.toString().padStart(2, '0')})`);
      console.log(`  - End minutes: ${endM} (${eh}:${em.toString().padStart(2, '0')})`);
      console.log(`  - Current minutes: ${currentMinutes}`);
      console.log(`  - Is within time range: ${currentMinutes >= startM && currentMinutes <= endM}`);
      console.log(`  - Is active: ${b.active ?? true}`);
      
      const isWithinTime = currentMinutes >= startM && currentMinutes <= endM;
      const isActive = b.active ?? true;
      
      if (isWithinTime && isActive) {
        console.log(`findActiveClassroomBookingNow: ✅ Found active booking: ${b.id}`);
        return b;
      } else {
        console.log(`findActiveClassroomBookingNow: ❌ Booking ${b.id} not active (within time: ${isWithinTime}, active: ${isActive})`);
      }
    }
    
    console.log(`findActiveClassroomBookingNow: No active booking found for current time`);
    return null;
  } catch (error) {
    console.error("Error finding active classroom booking:", error);
    throw error;
  }
};

// Timetable (weekly recurring lectures) utilities

// List weekly timetable entries for a classroom
export const listTimetableEntriesByClassroom = async (tenantId, classroomId) => {
  const colRef = collection(firestore, "TimetableEntries");
  const qy = query(
    colRef,
    where("tenantId", "==", tenantId),
    where("classroomId", "==", classroomId)
  );
  const res = await getDocs(qy);
  return res.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Check weekly overlap: same weekday, and time ranges overlap
export const isWeeklySlotAvailable = async (tenantId, classroomId, weekday, startTime, endTime, excludeId) => {
  const entries = await listTimetableEntriesByClassroom(tenantId, classroomId);
  const requestStart = toMinutes(startTime);
  const requestEnd = toMinutes(endTime);
  for (const e of entries) {
    if (excludeId && e.id === excludeId) continue;
    if (e.weekday !== weekday) continue;
    const eStart = toMinutes(e.startTime);
    const eEnd = toMinutes(e.endTime);
    if (requestStart < eEnd && requestEnd > eStart) {
      return { available: false, conflict: e };
    }
  }
  return { available: true };
};

// Helper: HH:MM or HH:MM:SS to minutes
const toMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Compute next occurrence Date for a weekly entry from now
export const getNextOccurrenceDate = (weekday, startTime) => {
  const now = new Date();
  const result = new Date(now);
  const currentWeekday = now.getDay(); // 0-6 Sun-Sat
  let delta = weekday - currentWeekday;
  if (delta < 0) delta += 7;
  result.setDate(now.getDate() + delta);
  const [sh, sm] = (startTime || "00:00").split(":").map(Number);
  result.setHours(sh || 0, sm || 0, 0, 0);
  // If today and time already passed, move to next week
  if (delta === 0 && result <= now) {
    result.setDate(result.getDate() + 7);
  }
  return result;
};

// Create a timetable entry with overlap prevention and date range support
export const createTimetableEntry = async ({ tenantId, classroomId, title, weekday, startTime, endTime, startDate, endDate }) => {
  if (tenantId == null || classroomId == null) throw new Error("Missing tenantId or classroomId");
  if (weekday == null || startTime == null || endTime == null || !title) throw new Error("Missing required fields");
  const { available, conflict } = await isWeeklySlotAvailable(tenantId, classroomId, weekday, startTime, endTime);
  if (!available) {
    const err = new Error("Time overlaps an existing lecture");
    err.conflict = conflict;
    throw err;
  }
  const colRef = collection(firestore, "TimetableEntries");
  const docRef = await addDoc(colRef, {
    tenantId,
    classroomId,
    title,
    weekday,
    startTime,
    endTime,
    startDate: startDate || null, // Date range start (optional)
    endDate: endDate || null, // Date range end (optional)
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    active: true,
  });
  return { id: docRef.id };
};

// Update a timetable entry (only if next occurrence hasn't started yet)
export const updateTimetableEntry = async (entryId, updates) => {
  const ref = doc(firestore, "TimetableEntries", entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Entry not found");
  const data = { id: snap.id, ...snap.data() };
  const nextStart = getNextOccurrenceDate(updates.weekday ?? data.weekday, updates.startTime ?? data.startTime);
  if (new Date() >= nextStart) {
    throw new Error("Cannot modify a lecture that has already started or completed");
  }
  await updateDoc(ref, { ...updates, updatedAt: new Date().toISOString() });
  return true;
};

// Get a timetable entry by ID
export const getTimetableEntryById = async (entryId) => {
  const ref = doc(firestore, "TimetableEntries", entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// Delete a timetable entry (only if next occurrence hasn't started yet)
export const deleteTimetableEntry = async (entryId) => {
  const ref = doc(firestore, "TimetableEntries", entryId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Entry not found");
  const data = { id: snap.id, ...snap.data() };
  const nextStart = getNextOccurrenceDate(data.weekday, data.startTime);
  if (new Date() >= nextStart) {
    throw new Error("Cannot delete a lecture that has already started or completed");
  }
  await updateDoc(ref, { active: false, updatedAt: new Date().toISOString() });
  return true;
};

// Supervisor Management Functions

// Get user by email
export const getUserByEmail = async (userEmail) => {
  try {
    const q = query(collection(firestore, "Users"), where("userEmail", "==", userEmail));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const userDoc = snapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.error("Error getting user by email:", error);
    return null;
  }
};

// Check if user has a supervisor
export const hasSupervisor = async (userEmail) => {
  const user = await getUserByEmail(userEmail);
  return user && user.supervisorId && user.supervisorId !== "";
};

// Get supervisor for a user
export const getSupervisor = async (userEmail) => {
  const user = await getUserByEmail(userEmail);
  if (!user || !user.supervisorId) return null;
  return await getUserByEmail(user.supervisorId);
};

// Assign supervisor to a user (ensures only one supervisor per user)
export const assignSupervisor = async (userEmail, supervisorEmail) => {
  try {
    // Query user by email instead of assuming document ID
    const userQ = query(collection(firestore, "Users"), where("userEmail", "==", userEmail));
    const userSnapshot = await getDocs(userQ);
    if (userSnapshot.empty) {
      throw new Error("User not found");
    }
    const userDoc = userSnapshot.docs[0];
    const userData = userDoc.data();
    
    // Check if user already has a different supervisor
    if (userData.supervisorId && userData.supervisorId !== supervisorEmail) {
      throw new Error(`User already has a supervisor assigned: ${userData.supervisorId}. Please remove the existing supervisor first.`);
    }
    
    const userRef = doc(firestore, "Users", userDoc.id);
    await updateDoc(userRef, {
      supervisorId: supervisorEmail,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error("Error assigning supervisor:", error);
    throw error;
  }
};

// Remove supervisor from a user
export const removeSupervisor = async (userEmail) => {
  try {
    // Query user by email instead of assuming document ID
    const userQ = query(collection(firestore, "Users"), where("userEmail", "==", userEmail));
    const userSnapshot = await getDocs(userQ);
    if (userSnapshot.empty) {
      throw new Error("User not found");
    }
    const userDoc = userSnapshot.docs[0];
    const userRef = doc(firestore, "Users", userDoc.id);
    await updateDoc(userRef, {
      supervisorId: null,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error("Error removing supervisor:", error);
    throw error;
  }
};

// Get all users supervised by a supervisor (both individual and team assignments)
export const getSupervisedUsers = async (supervisorEmail) => {
  // Get users with direct supervisor assignment
  const directQ = query(collection(firestore, "Users"), where("supervisorId", "==", supervisorEmail));
  const directSnapshot = await getDocs(directQ);
  const directUsers = directSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  
  // Get teams where this supervisor is assigned
  const teamsQ = query(collection(firestore, "Groups"), where("supervisorEmail", "==", supervisorEmail));
  const teamsSnapshot = await getDocs(teamsQ);
  const teams = teamsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  
  // Get all team member emails
  const teamMemberEmails = new Set();
  teams.forEach(team => {
    if (team.members && Array.isArray(team.members)) {
      team.members.forEach(email => teamMemberEmails.add(email));
    }
  });
  
  // Get user documents for team members
  const teamUsersPromises = Array.from(teamMemberEmails).map(async (email) => {
    const userQ = query(collection(firestore, "Users"), where("userEmail", "==", email));
    const userSnapshot = await getDocs(userQ);
    if (!userSnapshot.empty) {
      const userDoc = userSnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  });
  
  const teamUsers = (await Promise.all(teamUsersPromises)).filter(u => u !== null);
  
  // Combine direct users and team users, removing duplicates
  const allUsers = [...directUsers];
  teamUsers.forEach(teamUser => {
    if (!allUsers.find(u => u.userEmail === teamUser.userEmail)) {
      allUsers.push(teamUser);
    }
  });
  
  return allUsers;
};

// Get pending time bookings for a supervisor
export const getPendingTimeBookings = async (supervisorEmail, tenantId, userRole = null) => {
  try {
    // For admins and SuperAdmins, fetch ALL pending bookings for the tenant
    if (userRole === "admin" || userRole === "SuperAdmin") {
      const q = query(
        collection(firestore, "Timer"),
        where("tenantId", "==", tenantId),
        where("approvalStatus", "==", "pending")
      );
      const snapshot = await getDocs(q);
      const allBookings = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      
      console.log(`[getPendingTimeBookings] Admin/SuperAdmin: ${supervisorEmail}, Total pending bookings: ${allBookings.length}`);
      
      return allBookings;
    }
    
    // For regular supervisors, get bookings from supervised users
    const supervisedUsers = await getSupervisedUsers(supervisorEmail);
    const supervisedEmails = supervisedUsers.map(u => u.userEmail);
    
    console.log(`[getPendingTimeBookings] Supervisor: ${supervisorEmail}, Supervised users: ${supervisedEmails.length}`, supervisedEmails);
    
    // Get all pending bookings for the tenant
    const q = query(
      collection(firestore, "Timer"),
      where("tenantId", "==", tenantId),
      where("approvalStatus", "==", "pending")
    );
    const snapshot = await getDocs(q);
    const allBookings = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    
    console.log(`[getPendingTimeBookings] Total pending bookings: ${allBookings.length}`);
    
    // Filter to only bookings from supervised users (include supervisor themselves)
    const supervisedEmailsWithSelf = [...supervisedEmails, supervisorEmail];
    const supervisedBookings = allBookings.filter(booking => supervisedEmailsWithSelf.includes(booking.userEmail));
    
    console.log(`[getPendingTimeBookings] Bookings from supervised users (including self): ${supervisedBookings.length}`);
    
    // Also get bookings with release flag (old entries without supervisor)
    const releaseQ = query(
      collection(firestore, "Timer"),
      where("tenantId", "==", tenantId),
      where("releaseFlag", "==", true),
      where("approvalStatus", "==", "pending")
    );
    const releaseSnapshot = await getDocs(releaseQ);
    const releaseBookings = releaseSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    
    console.log(`[getPendingTimeBookings] Release flag bookings: ${releaseBookings.length}`);
    
    // Combine both sets of bookings
    const combinedBookings = [...supervisedBookings, ...releaseBookings];
    
    // Remove duplicates based on booking ID
    const uniqueBookings = combinedBookings.filter((booking, index, self) =>
      index === self.findIndex(b => b.id === booking.id)
    );
    
    console.log(`[getPendingTimeBookings] Final unique bookings: ${uniqueBookings.length}`);
    
    return uniqueBookings;
  } catch (error) {
    console.error("[getPendingTimeBookings] Error:", error);
    throw error;
  }
};

// Approve a time booking
export const approveTimeBooking = async (bookingId) => {
  const bookingRef = doc(firestore, "Timer", bookingId);
  await updateDoc(bookingRef, {
    approvalStatus: "approved",
    approvedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return true;
};

// Reject a time booking
export const rejectTimeBooking = async (bookingId, reason = "") => {
  const bookingRef = doc(firestore, "Timer", bookingId);
  await updateDoc(bookingRef, {
    approvalStatus: "rejected",
    rejectionReason: reason,
    rejectedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return true;
};

// Duplicate a team and assign to new supervisor
export const duplicateTeam = async (teamId, newSupervisorEmail, tenantId) => {
  const teamRef = doc(firestore, "Groups", teamId);
  const teamSnap = await getDoc(teamRef);
  if (!teamSnap.exists()) throw new Error("Team not found");
  
  const teamData = teamSnap.data();
  const newTeamData = {
    ...teamData,
    supervisorEmail: newSupervisorEmail,
    tenantId: tenantId,
    duplicatedFrom: teamId,
    duplicatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString()
  };
  
  // Remove the id field if it exists
  delete newTeamData.id;
  
  const newTeamRef = await addDoc(collection(firestore, "Groups"), newTeamData);
  return { id: newTeamRef.id };
};

// Get all teams for a tenant
export const getTeamsForTenant = async (tenantId) => {
  const q = query(collection(firestore, "Groups"), where("tenantId", "==", tenantId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Update a team
export const updateTeam = async (teamId, updates) => {
  try {
    const teamRef = doc(firestore, "Groups", teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) throw new Error("Team not found");
    
    await updateDoc(teamRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
    
    // If members or supervisor changed, update individual supervisor assignments
    const teamData = teamSnap.data();
    const oldMembers = teamData.members || [];
    const newMembers = updates.members || oldMembers;
    const oldSupervisor = teamData.supervisorEmail;
    const newSupervisor = updates.supervisorEmail !== undefined ? updates.supervisorEmail : oldSupervisor;
    
    // Handle supervisor changes
    if (oldSupervisor !== newSupervisor) {
      // Remove old supervisor from old members
      if (oldSupervisor) {
        const removalPromises = oldMembers.map(async (memberEmail) => {
          try {
            const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
            const userSnapshot = await getDocs(userQ);
            if (!userSnapshot.empty) {
              const userDoc = userSnapshot.docs[0];
              const userData = userDoc.data();
              if (userData.supervisorId === oldSupervisor) {
                const userRef = doc(firestore, "Users", userDoc.id);
                await updateDoc(userRef, {
                  supervisorId: null,
                  updatedAt: new Date().toISOString()
                });
              }
            }
          } catch (error) {
            console.error(`Error removing supervisor from ${memberEmail}:`, error);
          }
        });
        await Promise.all(removalPromises);
      }
      
      // Assign new supervisor to new members
      if (newSupervisor && newMembers.length > 0) {
        const assignmentPromises = newMembers.map(async (memberEmail) => {
          try {
            const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
            const userSnapshot = await getDocs(userQ);
            if (!userSnapshot.empty) {
              const userDoc = userSnapshot.docs[0];
              const userData = userDoc.data();
              if (userData.role !== "SuperAdmin" && userData.role !== "admin") {
                const userRef = doc(firestore, "Users", userDoc.id);
                await updateDoc(userRef, {
                  supervisorId: newSupervisor,
                  updatedAt: new Date().toISOString()
                });
              }
            }
          } catch (error) {
            console.error(`Error assigning supervisor to ${memberEmail}:`, error);
          }
        });
        await Promise.all(assignmentPromises);
      }
    } else if (newSupervisor) {
      // Members changed but supervisor same - update assignments for new members only
      const addedMembers = newMembers.filter(m => !oldMembers.includes(m));
      const removedMembers = oldMembers.filter(m => !newMembers.includes(m));
      
      // Add supervisor to new members
      if (addedMembers.length > 0) {
        const assignmentPromises = addedMembers.map(async (memberEmail) => {
          try {
            const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
            const userSnapshot = await getDocs(userQ);
            if (!userSnapshot.empty) {
              const userDoc = userSnapshot.docs[0];
              const userData = userDoc.data();
              if (userData.role !== "SuperAdmin" && userData.role !== "admin") {
                const userRef = doc(firestore, "Users", userDoc.id);
                await updateDoc(userRef, {
                  supervisorId: newSupervisor,
                  updatedAt: new Date().toISOString()
                });
              }
            }
          } catch (error) {
            console.error(`Error assigning supervisor to ${memberEmail}:`, error);
          }
        });
        await Promise.all(assignmentPromises);
      }
      
      // Remove supervisor from removed members (only if they have this supervisor)
      if (removedMembers.length > 0) {
        const removalPromises = removedMembers.map(async (memberEmail) => {
          try {
            const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
            const userSnapshot = await getDocs(userQ);
            if (!userSnapshot.empty) {
              const userDoc = userSnapshot.docs[0];
              const userData = userDoc.data();
              if (userData.supervisorId === newSupervisor) {
                const userRef = doc(firestore, "Users", userDoc.id);
                await updateDoc(userRef, {
                  supervisorId: null,
                  updatedAt: new Date().toISOString()
                });
              }
            }
          } catch (error) {
            console.error(`Error removing supervisor from ${memberEmail}:`, error);
          }
        });
        await Promise.all(removalPromises);
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error updating team:", error);
    throw error;
  }
};

// Delete a team
export const deleteTeam = async (teamId) => {
  try {
    const teamRef = doc(firestore, "Groups", teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) throw new Error("Team not found");
    
    const teamData = teamSnap.data();
    const members = teamData.members || [];
    const supervisorEmail = teamData.supervisorEmail;
    
    // Remove supervisor from team members before deleting team
    if (supervisorEmail && members.length > 0) {
      const removalPromises = members.map(async (memberEmail) => {
        try {
          const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
          const userSnapshot = await getDocs(userQ);
          if (!userSnapshot.empty) {
            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();
            // Only remove if this user has this specific supervisor
            if (userData.supervisorId === supervisorEmail) {
              const userRef = doc(firestore, "Users", userDoc.id);
              await updateDoc(userRef, {
                supervisorId: null,
                updatedAt: new Date().toISOString()
              });
            }
          }
        } catch (error) {
          console.error(`Error removing supervisor from ${memberEmail}:`, error);
        }
      });
      await Promise.all(removalPromises);
    }
    
    // Delete the team
    await deleteDoc(teamRef);
    return true;
  } catch (error) {
    console.error("Error deleting team:", error);
    throw error;
  }
};

// Assign supervisor to a team (also assigns individually to each team member)
export const assignSupervisorToTeam = async (teamId, supervisorEmail, tenantId) => {
  try {
    const teamRef = doc(firestore, "Groups", teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) throw new Error("Team not found");
    
    const teamData = teamSnap.data();
    const members = teamData.members || [];
    
    // Update team with supervisor
    await updateDoc(teamRef, {
      supervisorEmail: supervisorEmail || null,
      updatedAt: new Date().toISOString()
    });
    
    // Assign supervisor individually to each team member
    if (supervisorEmail && members.length > 0) {
      const assignmentPromises = members.map(async (memberEmail) => {
        try {
          // Query user by email instead of assuming document ID
          const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
          const userSnapshot = await getDocs(userQ);
          if (!userSnapshot.empty) {
            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();
            // Only assign if user is not SuperAdmin or admin
            if (userData.role !== "SuperAdmin" && userData.role !== "admin") {
              const userRef = doc(firestore, "Users", userDoc.id);
              await updateDoc(userRef, {
                supervisorId: supervisorEmail,
                updatedAt: new Date().toISOString()
              });
            }
          }
        } catch (error) {
          console.error(`Error assigning supervisor to ${memberEmail}:`, error);
        }
      });
      
      await Promise.all(assignmentPromises);
    }
    
    return true;
  } catch (error) {
    console.error("Error assigning supervisor to team:", error);
    throw error;
  }
};

// Remove supervisor from a team (also removes from individual team members)
export const removeSupervisorFromTeam = async (teamId, tenantId) => {
  try {
    const teamRef = doc(firestore, "Groups", teamId);
    const teamSnap = await getDoc(teamRef);
    if (!teamSnap.exists()) throw new Error("Team not found");
    
    const teamData = teamSnap.data();
    const members = teamData.members || [];
    const supervisorEmail = teamData.supervisorEmail;
    
    // Remove supervisor from team
    await updateDoc(teamRef, {
      supervisorEmail: null,
      updatedAt: new Date().toISOString()
    });
    
    // Remove supervisor individually from each team member (only if they have this supervisor)
    if (supervisorEmail && members.length > 0) {
      const removalPromises = members.map(async (memberEmail) => {
        try {
          // Query user by email instead of assuming document ID
          const userQ = query(collection(firestore, "Users"), where("userEmail", "==", memberEmail));
          const userSnapshot = await getDocs(userQ);
          if (!userSnapshot.empty) {
            const userDoc = userSnapshot.docs[0];
            const userData = userDoc.data();
            // Only remove if this user has this specific supervisor
            if (userData.supervisorId === supervisorEmail) {
              const userRef = doc(firestore, "Users", userDoc.id);
              await updateDoc(userRef, {
                supervisorId: null,
                updatedAt: new Date().toISOString()
              });
            }
          }
        } catch (error) {
          console.error(`Error removing supervisor from ${memberEmail}:`, error);
        }
      });
      
      await Promise.all(removalPromises);
    }
    
    return true;
  } catch (error) {
    console.error("Error removing supervisor from team:", error);
    throw error;
  }
};

// Utility function to add release flag to old timebookings without supervisor
// This should be run once to migrate old data
export const addReleaseFlagToOldBookings = async (tenantId) => {
  try {
    // Get all timebookings for the tenant that don't have a supervisorId set
    const q = query(
      collection(firestore, "Timer"),
      where("tenantId", "==", tenantId)
    );
    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    
    let updatedCount = 0;
    const updatePromises = bookings.map(async (booking) => {
      // Check if booking doesn't have supervisorId and doesn't already have releaseFlag
      if (!booking.releaseFlag && booking.approvalStatus === "pending") {
        // Check if user has a supervisor
        const user = await getUserByEmail(booking.userEmail);
        if (!user || !user.supervisorId) {
          // This booking is from a user without a supervisor - add release flag
          const bookingRef = doc(firestore, "Timer", booking.id);
          await updateDoc(bookingRef, {
            releaseFlag: true,
            updatedAt: new Date().toISOString()
          });
          updatedCount++;
        }
      }
    });
    
    await Promise.all(updatePromises);
    return { updatedCount, totalBookings: bookings.length };
  } catch (error) {
    console.error("Error adding release flags to old bookings:", error);
    throw error;
  }
};